/**
 * @file page-content-editor.tsx
 * @module app/modules/setting/components/page-content-editor
 * @description Shared presentational layout for single-content Pages
 * sub-tabs (language select + rich text editor + save button). Extracted
 * from the duplicated JSX in pages-terms-condition.tsx and
 * pages-privacy-policy.tsx.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.3.3
 * @version 2.1.2
 */

import { Button, Col, Row } from 'react-bootstrap';
import TextEditor from '../../_common/components/text-editor';
import Loader from '../../../../Global/loader';
import { CustomSelect } from '../../../custom/Select/CustomSelect';

const LANGUAGE_OPTIONS = [
  { label: 'English', title: 'English', value: 'en' },
  { label: 'Swahili', title: 'Swahili', value: 'sw' },
];

interface IPageContentEditorProps {
  title: string;
  contentData: any;
  loading: boolean;
  fetchLoading: boolean;
  selectedLanguage: any;
  hasViewPermission: boolean;
  onDataChange: (data: any) => void;
  onLanguageChange: (option: any) => void;
  onSave: (data: any) => void;
}

/**
 * Renders the header, language selector, rich text editor, and save button
 * shared by every single-content Pages sub-tab.
 * @param {IPageContentEditorProps} props Content, state, and handlers for the editor.
 * @returns {JSX.Element} Page content editor layout.
 */
const PageContentEditor = ({
  title,
  contentData,
  loading,
  fetchLoading,
  selectedLanguage,
  hasViewPermission,
  onDataChange,
  onLanguageChange,
  onSave,
}: IPageContentEditorProps) => {
  return (
    <div className="p-5">
      <Row className="align-items-center">
        <Col md className="align-self-center mb-5">
          <Row className="align-items-center">
            <Col lg={8} md={6}>
              <h1 className="fs-22 fw-bolder">{title}</h1>
            </Col>
            <Col lg={4} md={6}>
              <CustomSelect
                backgroundColor="white"
                placeholder="Select Language"
                onChange={onLanguageChange}
                value={selectedLanguage}
                options={LANGUAGE_OPTIONS}
              />
            </Col>
          </Row>
        </Col>
        {fetchLoading ? (
          <div className="d-flex justify-content-center m-4">
            <Loader loading={fetchLoading} />
          </div>
        ) : (
          <>
            <TextEditor data={contentData} handleDataChange={onDataChange} />
            <Col xs="auto" className="pt-7">
              <Button className="uw-btn uw-btn-primary uw-btn-lg"
                variant="primary"
                size="lg"
                onClick={() => onSave(contentData)}
                disabled={loading || hasViewPermission}
              >
                {!loading && <span className="indicator-label">Save Changes</span>}
                {loading && (
                  <span className="indicator-progress" style={{ display: 'block' }}>
                    Please wait...
                    <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                  </span>
                )}
              </Button>
            </Col>
          </>
        )}
      </Row>
    </div>
  );
};

export default PageContentEditor;
